import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid
} from '@mui/material';
import styled from '@emotion/styled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { articles } from '../data/articles';

// Styled components
const BlogSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'url(/assets/images/bg/choose-bg.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  padding: '100px 0',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '60px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: '20px',
  fontWeight: 700,
  color: '#000000',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 0,
    height: '3px',
    width: '50px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '1200px',
  position: 'relative',
  '.slick-slide': {
    padding: '10px',
  },
  '.slick-dots': {
    bottom: '-40px',
    '& li button:before': {
      fontSize: '12px',
    },
    '& li.slick-active button:before': {
      color: theme.palette.primary.main,
    }
  },
  '.slick-prev, .slick-next': {
    zIndex: 1,
    '&:before': {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none !important',
    }
  }
}));

const ArticleSlide = styled(Box)(({ theme }) => ({
  padding: '20px',
  outline: 'none',
}));

const ArticleCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.2)',
  height: '100%',
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
  }
}));

const ArticleImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '240px',
}));

const ArticleImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'all 0.5s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  }
}));

const ArticleCategory = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  left: '20px',
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: '5px 15px',
  borderRadius: '30px',
  fontSize: '14px',
  fontWeight: 500,
}));

const ArticleContent = styled(Box)(({ theme }) => ({
  padding: '25px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const ArticleMeta = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: '15px',
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    marginRight: '20px',
    marginBottom: '5px',
    color: theme.palette.text.secondary,
    fontSize: '14px',
    '& svg': {
      fontSize: '16px',
      marginRight: '5px',
    }
  }
}));

const ArticleTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '15px',
  fontWeight: 600,
  color: '#333',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: theme.palette.primary.main,
  }
}));

const ArticleExcerpt = styled(Typography)(({ theme }) => ({
  marginBottom: '20px',
  flexGrow: 1,
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: '8px 20px',
  borderRadius: '5px',
  fontWeight: 600,
  textTransform: 'none',
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

const SliderArrow = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  }
}));

const PrevArrow = styled(SliderArrow)(({ theme }) => ({
  left: '-50px',
}));

const NextArrow = styled(SliderArrow)(({ theme }) => ({
  right: '-50px',
}));

function Blog() {
  // Custom arrow components for the slider
  const PrevArrowComponent = (props) => {
    const { onClick } = props;
    return (
      <PrevArrow onClick={onClick}>
        <ArrowBackIcon />
      </PrevArrow>
    );
  };

  const NextArrowComponent = (props) => {
    const { onClick } = props;
    return (
      <NextArrow onClick={onClick}>
        <ArrowForwardIcon />
      </NextArrow>
    );
  };

  // Slider settings
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    prevArrow: <PrevArrowComponent />,
    nextArrow: <NextArrowComponent />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  // Function to extract excerpt from article content
  const getExcerpt = (content) => {
    const paragraphs = content.filter(item => item.type === 'paragraph');
    if (paragraphs.length > 0) {
      const text = paragraphs[0].text;
      return text.length > 120 ? `${text.substring(0, 120)}...` : text;
    }
    return '';
  };

  // Function to parse date strings and sort articles by date (newest first)
  const sortArticlesByDate = (articlesArray) => {
    return [...articlesArray].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Sort in descending order (newest first)
    });
  };

  // Sort the articles from newest to oldest
  const sortedArticles = sortArticlesByDate(articles);

  return (
    <BlogSection id="rs-blog">
      <Container maxWidth="lg">
        <SectionTitle>
          <Title variant="h2">
            Latest blog posts
          </Title>
          <Typography variant="body1" sx={{ maxWidth: '700px', margin: '0 auto', mt: 2, color: '#000000' }}>
            Stay updated with our insights on cybersecurity trends, best practices, and industry developments.
          </Typography>
        </SectionTitle>

        <SliderContainer>
          <Slider {...settings}>
            {sortedArticles.map((article) => (
              <ArticleSlide key={article.id}>
                <ArticleCard>
                  <ArticleImageContainer>
                    <ArticleImage src={article.image} alt={article.title} />
                    <ArticleCategory>
                      {article.category}
                    </ArticleCategory>
                  </ArticleImageContainer>
                  <ArticleContent>
                    <ArticleMeta>
                      <Box>
                        <CalendarTodayIcon />
                        {article.date}
                      </Box>
                      <Box>
                        <PersonIcon />
                        {article.author}
                      </Box>
                      <Box>
                        <CategoryIcon />
                        {article.category}
                      </Box>
                    </ArticleMeta>
                    <ArticleTitle variant="h5">
                      {article.title}
                    </ArticleTitle>
                    <ArticleExcerpt variant="body2" color="text.secondary">
                      {getExcerpt(article.content)}
                    </ArticleExcerpt>
                    <ReadMoreButton component={RouterLink} to={`/article/${article.id}`}>
                      Read More
                    </ReadMoreButton>
                  </ArticleContent>
                </ArticleCard>
              </ArticleSlide>
            ))}
          </Slider>
        </SliderContainer>
      </Container>
    </BlogSection>
  );
}

export default Blog;
